import { useMemo, useState, type RefObject } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { toast } from "@/components/shared";
import { getErrorMessage } from "@/lib/api-client";
import { downloadQvdRunLogs } from "@/lib/api/qvd-analysis";
import { qvdRunLogsInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { RunLogsDrawer, RunLogsList, type RunLogLevel } from "@/components/shared/RunLogsView";

// The run's worker execution-log stream (lineage metrics, retry warnings, phase
// summaries), bound to the QVD run-log source. Presentation lives in the shared
// RunLogsList; only the data wiring (query + export) is QVD-specific.
function QvdRunLogsList({ runId, active }: { runId: string; active: boolean }) {
  const { t } = useTranslation("qvd-analysis");
  const [level, setLevel] = useState<RunLogLevel>("info");

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      qvdRunLogsInfiniteQueryOptions(
        runId,
        { level: level === "info" ? undefined : level },
        active,
      ),
    );

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  return (
    <RunLogsList
      ns="qvd-analysis"
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      active={active}
      level={level}
      onLevelChange={setLevel}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      onExport={() =>
        downloadQvdRunLogs(runId).catch((err) =>
          toast.error(getErrorMessage(err, t("toast.exportFailed"))),
        )
      }
    />
  );
}

export function QvdRunLogsDrawer({
  open,
  onOpenChange,
  runId,
  active,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  active: boolean;
  /** The "Logs" button, for the focus to return to when the drawer closes. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  return (
    <RunLogsDrawer
      open={open}
      onOpenChange={onOpenChange}
      ns="qvd-analysis"
      restoreFocusRef={restoreFocusRef}
    >
      <QvdRunLogsList runId={runId} active={active} />
    </RunLogsDrawer>
  );
}
